import { TextField as MUITextField, Typography } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ContentTitle from "components/common/ContentTitle";
import Steper from "components/common/Steper";
import ButtonController from "components/request-document/components/ButtonController";
import {
  StyledTableCell,
  useStyles
} from "components/request-document/components/Commons";
import EducationSection from "components/request-document/components/EducationSection";
import PersonalInfoSection from "components/request-document/components/PersonalInfoSection";
import SpouseSection from "components/request-document/components/SpouseSection";
import { useFormik } from "formik";
import nationalities from "i18n-nationality";
import {
  REQUEST_DOCUMENT_STEP,
  RoleId,
  STATUS_PROCESS
} from "libs/configs/constant";
import { SMART_T_STEP } from "libs/constant/Step";
import useModalLoading from "libs/hooks/useModalLoading";
import {
  defaultChildren,
  defaultEducationData,
  defaultSpouse,
  getDefaultRow,
  mapChildren,
  mapEducationData,
  mapSpouse,
  personalFieldResolver
} from "libs/utils/form";
import { formatDate } from "libs/utils/format";
import { convertMRZDate } from "libs/utils/mrzUtils";
import { get, pick } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addPersonal,
  addPersonalSlice,
  selectAddPersonal
} from "redux/modules/addPersonalSlice";
import { canEditForm } from "redux/modules/authSlice";
import { getMasterData, selectMasterData } from "redux/modules/masterSlice";
import { selectPassportState } from "redux/modules/passportSlice";
import { getPersonalDetail, selectPersonalDetail } from "redux/modules/personalSlice";
import { setSmartFormStep } from "redux/modules/uiSlice";
import {
  addSmartForm,
  addSmartFormSlice,
  getSmartForm,
  getSmartFormFieldConfig,
  selectAddSmartForm,
  selectCurrentSmartForm,
  selectCurrentSmartServiceId,
  selectDraftId,
  selectSmartFormFieldConfig
} from "redux/modules/visaForm";
import * as yup from "yup";

const TextField = React.memo(MUITextField);

export default function UserProfileStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { showLoading, hideLoading } = useModalLoading();
  const [isSaveDraft, setSaveDraft] = useState(false);
  const canEdit = useSelector(canEditForm);
  const passportState = useSelector(selectPassportState);
  const personalDetail = useSelector(selectPersonalDetail);
  const addPersonalState = useSelector(selectAddPersonal);
  const smartServiceId = useSelector(selectCurrentSmartServiceId);
  const masterData = useSelector(selectMasterData);
  const smartFormFieldConfig = useSelector(selectSmartFormFieldConfig);
  const draftId = useSelector(selectDraftId);
  const smartform = useSelector(selectCurrentSmartForm);
  const addSmartFormState = useSelector(selectAddSmartForm);

  const nationalityConfig = get(masterData, "nationalityConfig", []);
  const personalId = get(personalDetail, "personalID", "");
  const ocrFirstName = get(passportState, "detail.firstName", "");
  const ocrLastName = get(passportState, "detail.lastName", "");
  const ocrBirthDate = convertMRZDate(
    get(passportState, "detail.birthDate", "")
  );
  const nationalityAlpha3 = get(passportState, "detail.nationality", "");
  const nationalityName = nationalities.getName(nationalityAlpha3, "en");
  const ocrNationalityID = get(
    nationalityConfig.find((v) => v.nationalityName === nationalityName),
    "nationalityID",
    ""
  );
  const ocrExpirationDate = convertMRZDate(
    get(passportState, "detail.expirationDate", "")
  );
  const ocrSex = get(passportState, "detail.sex", "").slice(0, 1).toUpperCase();
  const ocrDocumentNumber = get(passportState, "detail.documentNumber", "");

  const personal = pick(get(smartform, "personal", {}), [
    "personalID",
    "firstname",
    "middleName",
    "lastName",
    "nationalityID",
    "birthDate",
    "email",
    "placeOfBirth",
    "gender",
    "passportNumber",
    "dateofIssue",
    "validuntil",
    "visatypeID",
  ]);
  const detailList = [
    "Marittal",
    "AddressInHome",
    "AddressInThai",
    "SubDistrict",
    "District",
    "Province",
    "Zipcode",
    "PhoneNumber",
    "Email",
    "Spouse",
    "Children",
    "EducationOther",
    "EducationId",
  ];

  const detail = get(smartform, "detail", [])
    .filter((field) => detailList.includes(field.smartName))
    .reduce((p, c) => ({ ...p, [c.smartName]: c }), {});

  useEffect(() => {
    dispatch(
      getMasterData({
        nationalityConfig: true,
        EducationConfig: true,
        CountryConfig: true,
      })
    );
    return () => {
      dispatch(addPersonalSlice.actions.clearAsyncState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (smartServiceId) {
      dispatch(getSmartFormFieldConfig({ smartserviceId: smartServiceId }));
      dispatch(
        getSmartForm({
          smartserviceId: smartServiceId,
          draftId,
          personalId,
        })
      );
    }
  }, [dispatch, draftId, personalDetail, personalId, smartServiceId]);

  useEffect(() => {
    if (!addPersonalState.isLoading) {
      if (addPersonalState.isSuccess) {
        dispatch(addPersonalSlice.actions.clearAsyncState());
      } else if (addPersonalState.isFailure) {
        hideLoading();
        alert(addPersonalState.error?.message);
        dispatch(addPersonalSlice.actions.clearAsyncState());
      }
    }
  }, [addPersonalState, dispatch, hideLoading]);

  useEffect(() => {
    if (!addSmartFormState.isLoading) {
      if (addSmartFormState.isSuccess) {
        hideLoading();
        dispatch(addSmartFormSlice.actions.clearAsyncState());
        if (!isSaveDraft) {
          dispatch(setSmartFormStep(SMART_T_STEP.PART_A_2));
        } else {
          dispatch(
            getSmartForm({
              smartserviceId: smartServiceId,
              draftId,
              personalId,
            })
          );
        }
      } else if (addSmartFormState.isFailure) {
        hideLoading();
        alert(addSmartFormState.error?.message);
        dispatch(addSmartFormSlice.actions.clearAsyncState());
      }
    }
  }, [
    addSmartFormState,
    dispatch,
    draftId,
    hideLoading,
    isSaveDraft,
    personalId,
    smartServiceId,
  ]);

  const validationSchema = yup.object({
    // personal: yup.object({
    //   personalID: yup.string(),
    //   fistName: yup.string().required("Fist name is a required field"),
    //   middleName: yup.string(),
    //   lastName: yup.string().required("Last name is a required field"),
    //   nationalityID: yup.string().required("Nationality is a required field"),
    //   birthDate: yup.string().required("Birthdate is a required field"),
    //   email: yup.string().required("Email is a required field"),
    //   placeOfBirth: yup.string().required("Placeofbirth is a required field"),
    //   gender: yup.string().required("Gender is a required field"),
    //   passportNumber: yup
    //     .string()
    //     .required("Passportnumber is a required field"),
    //   dateofIssue: yup.string().required("Date of issue is a required field"),
    //   validuntil: yup.string().required("Valid until is a required field"),
    // }),
    // document: yup.object({
    //   Marittal: yup.string().required("Marittal is a required field"),
    //   AddressInHome: yup
    //     .string()
    //     .required("Address in home is a required field"),
    //   AddressInThai: yup
    //     .string()
    //     .required("Address in thai is a required field"),
    //   PhoneNumber: yup.string().required("PhoneNumber is a required field"),
    //   Email: yup
    //     .string()
    //     .email("Invalid email format")
    //     .required("Email is a required field"),
    //   Spouse: yup.array().of(
    //     yup.object().shape({
    //       SpouseName: yup.string(),
    //       spouseId: yup.string().nullable(true),
    //       SpouseBirthDate: yup.string().when("SpouseName", {
    //         is: (name) => !!name,
    //         then: yup.string().required("BirthDate is required"),
    //       }),
    //       SpouseNationality: yup.string().when("SpouseName", {
    //         is: (name) => !!name,
    //         then: yup.string().required("Nationality is required"),
    //       }),
    //       SpouseOccupation: yup.string().when("SpouseName", {
    //         is: (name) => !!name,
    //         then: yup.string().required("Occupation is required"),
    //       }),
    //       Status: yup.boolean(),
    //     })
    //   ),
    //   Children: yup.array().of(
    //     yup.object().shape({
    //       ChildrenName: yup.string(),
    //       childrenId: yup.string().nullable(true),
    //       ChildrenBrithDate: yup.string().when("ChildrenName", {
    //         is: (name) => !!name,
    //         then: yup.string().required("BirthDate is required"),
    //       }),
    //       ChildrenNationality: yup.string().when("ChildrenName", {
    //         is: (name) => !!name,
    //         then: yup.string().required("Nationality is required"),
    //       }),
    //       ChildrenOccuption: yup.string().when("ChildrenName", {
    //         is: (name) => !!name,
    //         then: yup.string().required("Occupation is required"),
    //       }),
    //       Status: yup.boolean(),
    //     })
    //   ),
    //   EducationOther: yup.string(),
    //   EducationId: yup.object().shape({
    //     EducationConfigID: yup.string(),
    //     data: yup.array().of(
    //       yup.object().shape({
    //         educationName: yup.string().nullable(true),
    //         educationId: yup.string().nullable(true),
    //         // .when("educationName", {
    //         //   is: (name) => !!name,
    //         //   then: yup
    //         //     .string()
    //         //     .nullable(true)
    //         //     .required("File upload is required"),
    //         // }),
    //         FileID: yup
    //           .string()
    //           .nullable(true)
    //           .when("educationName", {
    //             is: (name) => !!name,
    //             then: yup
    //               .string()
    //               .nullable(true)
    //               .required("File upload is required"),
    //           }),
    //         YearOfGraduation: yup.string().when("educationName", {
    //           is: (name) => !!name,
    //           then: yup.string().required("Year of graduation is required"),
    //         }),
    //         NameOfEducation: yup.string().when("educationName", {
    //           is: (name) => !!name,
    //           then: yup.string().required("Name of education is required"),
    //         }),
    //         countryId: yup.string().when("educationName", {
    //           is: (name) => !!name,
    //           then: yup.string().required("Country is required"),
    //         }),
    //         CityID: yup.string(),
    //         FieldOfStudy: yup.string().when("educationName", {
    //           is: (name) => !!name,
    //           then: yup.string().required("Field of study is required"),
    //         }),
    //       })
    //     ),
    //   }),
    // }),
  });

  const callAPISubmit = async (values) => {
    showLoading();

    const defaultType = get(smartFormFieldConfig, "data.defaultType", []);
    const documentFields = Object.keys(values.document);
    const getFieldListConfig = defaultType.filter((field) =>
      documentFields.includes(field.smartName)
    );

    const getValue = (field) => {
      switch (field.smartName) {
        case "Spouse": {
          const spouseName = get(values, `document.${field.smartName}`, []);
          const value = spouseName.filter((v) => !!v.SpouseName);
          return value || null;
        }
        case "Children": {
          const childrenName = get(values, `document.${field.smartName}`, []);
          const value = childrenName.filter((v) => !!v.ChildrenName);
          return value || null;
        }
        case "EducationId": {
          const educationId = get(values, `document.${field.smartName}`, {
            EducationConfigID: null,
            data: [],
          });
          const data = educationId.data.filter((v) => !!v.educationName);
          return {
            EducationConfigID: educationId.EducationConfigID,
            data: data.length ? data : null,
          };
        }
        default:
          return get(values, `document.${field.smartName}`, null);
      }
    };

    const getSmartId = (field) =>
      get(detail, `${field.smartName}.smartId`, null);

    const prepareData = {
      draftId: draftId || null,
      smartserviceId: smartServiceId,
      personalId: personalId,
      survey: null, // get(smartform, "survey", null), // TODO wait for support case edit
      visaissuance: null, // get(smartform, "visaissuance", null), // TODO wait for support case edit
      detail: [
        ...getFieldListConfig.map((field) => ({
          smartName: field.smartName,
          smartId: getSmartId(field),
          partfromId: field.partfromId,
          configsmartId: field.configsmartId,
          linkTable: field.linkTable,
          value: getValue(field),
        })),
      ],
    };

    await dispatch(
      addPersonal({
        ...personalFieldResolver(values.personal),
        visatypeID: null,
      })
    );
    await dispatch(
      addSmartForm({
        data: prepareData,
      })
    );
  };

  const HasAddressInThai = !!get(detail, "AddressInThai.value", "");
  const isDraftDocument = smartform?.statausID === STATUS_PROCESS.Draft;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      personal: {
        personalID: get(personal, "personalID", ""),
        fistName: ocrFirstName || get(personal, "firstname", ""),
        middleName: get(personal, "middleName", "") || "",
        lastName: ocrLastName || get(personal, "lastName", ""),
        nationalityID:
          ocrNationalityID || get(personal, "nationalityID", "") || "",
        birthDate: formatDate(
          ocrBirthDate || get(personal, "birthDate", "") || ""
        ),
        email: get(personal, "email", "") || "",
        placeOfBirth: get(personal, "placeOfBirth", "") || "",
        gender: ocrSex || get(personal, "gender", "M") || "M",
        passportNumber:
          ocrDocumentNumber || get(personal, "passportNumber", "") || "",
        dateofIssue: formatDate(get(personal, "dateofIssue", "") || ""),
        validuntil: formatDate(
          ocrExpirationDate || get(personal, "validuntil", "") || ""
        ),
        visatypeID: null,
      },
      document: {
        Marittal: get(detail, "Marittal.value", "S"),
        AddressInHome: get(detail, "AddressInHome.value", ""),
        HasAddressInThai,
        AddressInThai: get(detail, "AddressInThai.value", ""),
        SubDistrict: get(detail, "SubDistrict.value", ""),
        District: get(detail, "District.value", ""),
        Province: get(detail, "Province.value", ""),
        Zipcode: get(detail, "Zipcode.value", ""),
        PhoneNumber: get(detail, "PhoneNumber.value", ""),
        Email: get(detail, "Email.value", ""),
        Spouse: getDefaultRow(detail, "Spouse.value", defaultSpouse).map(
          mapSpouse
        ),
        Children: getDefaultRow(detail, "Children.value", defaultChildren).map(
          mapChildren
        ),
        EducationOther: get(detail, "EducationOther.value", ""),
        EducationId: {
          EducationConfigID: null,
          data: getDefaultRow(
            detail,
            "EducationId.value.data",
            defaultEducationData
          ).map(mapEducationData),
        },
      },
    },
    validationSchema: isDraftDocument ? validationSchema : yup.object({}),
    onSubmit: async (values) => {
      setSaveDraft(false);
      callAPISubmit(values);
    },
  });

  useEffect(() => {

    const firstTimeSavePersonal = async () => {
      await dispatch(
        addPersonal({
          ...personalFieldResolver(formik.values.personal),
          visatypeID: null,
        })
      );
      await dispatch(getPersonalDetail({ personalId }))
    }

    if (
      nationalityConfig.length &&
      ocrDocumentNumber && ocrDocumentNumber !== get(personal, "passportNumber", "")) {
      firstTimeSavePersonal()
    }
    
  }, [nationalityConfig, ocrDocumentNumber])
  
  const Role = useSelector((e) => {
    return e.user.profile.roleId;
  });
  // Disabled all field if status != draft
  if (!isDraftDocument && Role !== RoleId.Admin) {
    formik.handleBlur = () => {};
    formik.handleChange = () => {};
    formik.setFieldValue = () => {};
  }

  return (
    <div className={classes.root}>
      <ContentTitle
        title="Application Form"
        subTitle="APPLICATION FOR QUALIFICATION ENDORSEMENT FOR SMART VISA “I”"
      />
      <Steper currentStep={1} stepInfo={REQUEST_DOCUMENT_STEP} />
      <form onSubmit={formik.handleSubmit}>
        <TableContainer style={{ marginTop: 35, borderRadius: 4 }}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <StyledTableCell colSpan={7}>
                  <Typography variant="h6" className={classes.tableTitle}>
                    Part A : Personal Information
                  </Typography>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <PersonalInfoSection formik={formik} />
              <SpouseSection
                defaultChildren={mapChildren(defaultChildren)}
                formik={formik}
              />
              <EducationSection
                defaultEducationData={mapEducationData(defaultEducationData)}
                formik={formik}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <ButtonController
          onClickSaveDraft={() => {
            if (!canEdit) {
              dispatch(setSmartFormStep(SMART_T_STEP.PART_A_2));
              return;
            }
            setSaveDraft(true);
            callAPISubmit(formik.values);
          }}
        />
      </form>
    </div>
  );
}
